import { useState, useEffect, useCallback } from "react";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

const token = () => localStorage.getItem("portalToken") || "";

const REHIRE_COLORS = {
  yes: { color: "#166534", bg: "#e8f5e9" },
  no: { color: "#991b1b", bg: "#fdecea" },
  unsure: { color: "#92400e", bg: "#fff8e1" },
};

function RehireBadge({ value }) {
  if (!value) return <span style={{ fontSize: 12, color: "#9ca3af" }}>Not recorded</span>;
  const c = REHIRE_COLORS[value] || { color: "#555", bg: "#f5f5f5" };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: c.bg, color: c.color, textTransform: "capitalize" }}>
      {value}
    </span>
  );
}

export default function ArchivedCandidates() {
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (q) => {
    setLoading(true);
    setError("");
    try {
      const url = `${API_BASE}/api/admin/archived-candidates${q ? `?search=${encodeURIComponent(q)}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not load archived candidates");
      setCandidates(d.archived_candidates || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Archived Candidates</h1>
      <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 20 }}>
        Candidates whose files passed the 12-month POPIA retention cutoff. Their CV, contact details and documents have been
        permanently deleted — only their name, test scores, employment history and rehire decision are kept.
      </p>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)}
          style={{ flex: 1, maxWidth: 320, padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }}
        />
        <button
          onClick={() => load(search)}
          style={{ padding: "8px 16px", background: "#6a0080", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          Search
        </button>
      </div>

      {error && <p style={{ color: "#991b1b", fontSize: 13 }}>{error}</p>}
      {loading && <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>}

      {!loading && candidates.length === 0 && (
        <p style={{ color: "#9ca3af", fontSize: 13 }}>No archived candidates found.</p>
      )}

      {!loading && candidates.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ padding: "8px 6px" }}>Name</th>
              <th style={{ padding: "8px 6px" }}>Archived</th>
              <th style={{ padding: "8px 6px" }}>Test Score</th>
              <th style={{ padding: "8px 6px" }}>Employment Record</th>
              <th style={{ padding: "8px 6px" }}>Rehire?</th>
              <th style={{ padding: "8px 6px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ padding: "8px 6px", fontWeight: 600 }}>{c.full_name || "—"}</td>
                <td style={{ padding: "8px 6px", color: "#6b7280" }}>
                  {c.archived_at ? new Date(c.archived_at).toLocaleDateString() : "—"}
                </td>
                <td style={{ padding: "8px 6px", color: "#6b7280" }}>
                  {c.scorecard_pct != null ? `${c.scorecard_pct}%` : (c.latest_test_summary || "—")}
                </td>
                <td style={{ padding: "8px 6px", color: "#6b7280", maxWidth: 260 }}>{c.employment_summary || "—"}</td>
                <td style={{ padding: "8px 6px" }}><RehireBadge value={c.rehire_eligible} /></td>
                <td style={{ padding: "8px 6px", color: "#6b7280", maxWidth: 220 }}>{c.rehire_notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
